/* eslint-disable spire/export-styles */
import { MappedLink } from "@iewcasia/Services/CustomCategoryService";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { MainNavigationStyles } from "@insite/content-library/Widgets/Header/MainNavigation";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Popover from "@insite/mobius/Popover";
import debounce from "lodash/debounce";
import * as React from "react";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement | null>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}

// Verndale Customization
interface State {
    headingLinkHovered?: string;
    mainNavigationItems?: MappedLink[][];
    popOverIsOpen: boolean;
}

type Props = ThemeProps<BaseTheme> & OwnProps;

class MainNavigationItem extends React.Component<Props, State> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    // Verndale Customization
    constructor(props: Props) {
        super(props);
        this.state = {
            headingLinkHovered: "",
            mainNavigationItems: [],
            popOverIsOpen: false,
        };
    }

    componentDidMount(): void {
        // we set the mainNavigationItems state here initially with the link.mainNavigationColumns
        // before we can access the props

        const { link } = this.props;

        if (link.mainNavigationColumns) {
            this.setState({
                mainNavigationItems: link.mainNavigationColumns,
            });
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        this.setState({
            popOverIsOpen: false,
        });
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
        this.setState({
            popOverIsOpen: true,
        });
    };

    debouncedHandleMouseEnter = debounce(() => this.openPopover(), 500);

    handleOnMouseLeave = () => {
        this.closePopover();
        this.debouncedHandleMouseEnter.cancel();
    };
    // End Verndale Customization

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;
        const { headingLinkHovered } = this.state; // Verndale Customization
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
		const isCascading = hasChildren && (link.childrenType === "CascadingMenu" || link.childrenType === "CustomLinkList");
        const isMega = hasChildren && link.childrenType === "MegaMenu";

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className={`menu-item ${this.state.popOverIsOpen ? "menu-item--active" : ""} `}
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                    // Verndale Customization
                    onMouseEnter={() => {
                        this.debouncedHandleMouseEnter();
                    }}
                    onMouseLeave={() => {
                        this.handleOnMouseLeave();
                    }}
                    // End Verndale Customization
                    href={link.url}
                    target={link.openInNewWindow ? "_blank" : ""}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    className="cascading-menu"
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }

        if (isMega) {
            menuItem = (
                <Popover
                    className="mega-menu"
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: React.RefObject<HTMLUListElement | null>) => {
                        if (this.props.container.current?.offsetParent) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const cartWidth = 150;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return (
                                    parentPosition.width + cartWidth <= value &&
                                    parentPosition.width + cartWidth > maxWidths[index - 1]
                                );
                            });

                            const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;

                            return {
                                top,
                                position: "fixed",
                                // Verndale Customization: iewc-1000
                                left: 0,
                                width: `100vw`,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                    // Verndale Customization -- Remove toggle and add onMouseEnter and onMouseLeave
                    toggle={false}
                    onMouseEnter={() => {
                        this.openPopover();
                    }}
                    onMouseLeave={() => {
                        this.setState({
                            headingLinkHovered: ``,
                        });
                        // we close the popover on mouse leave
                        this.closePopover();
                    }}
                    // End Verndale Customization
                >
                    {/* Verndale Customization */}

                    <GridContainer
                        className="mega-menu-grid-container mega-menu-grid-container--title"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                        gap={0}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => {
                                return (
                                    <GridItem
                                        className="mega-menu-grid-item"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        {...styles.megaMenuGridItem}
                                        as="li"
                                        onMouseOver={() => {
                                            this.setState({	   
                                                headingLinkHovered: `${child.title}`,
                                            });
                                        }}
                                    >
                                        <Link
                                            className="mega-menu-heading-link"
                                            {...styles.megaMenuHeading}
                                            href={child.url}
                                            onClick={this.closePopover}
                                        >
                                            {child.title}
                                        </Link>
                                    </GridItem>
                                );
                            })}
                    </GridContainer>
                    <GridContainer
                        className="mega-menu-grid-container mega-menu-grid-container--content"
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{
                            ...styles.megaMenuGridContainer?.offsetProps,
                            as: "ul",
                        }}
                        gap={0}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation).map((child, index) => {
                                const { mainNavigationColumns, numberOfColumns, additionalContent } = child;
                                if (!mainNavigationColumns) {
                                    return null;
                                }

                                return (
                                    <GridItem
                                        className="mega-menu-grid-item"
                                        width={12}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        {...styles.megaMenuGridItem}
                                        as="li"
                                        style={{
                                            display:
                                                headingLinkHovered === `${child.title}` ||
                                                (headingLinkHovered === "" && index === 0)
                                                    ? "flex"
                                                    : "none",
                                        }}
                                    >
                                        <GridContainer
                                            className="mega-menu-grid-container mega-menu-grid-container--children"
                                            data-test-selector={`linksFor_${child.title}`}
                                            {...styles.megaMenuGridContainer}
                                            offsetProps={{
                                                ...styles.megaMenuGridContainer?.offsetProps,
                                                as: "ul",
                                            }}
                                            gap={0}
                                        >
                                            {mainNavigationColumns.map((columnItems, index) => {
                                                return (
                                                    <GridItem
                                                        className="mega-menu-grid-item mega-menu-grid-item--column"
                                                        width={(12 / numberOfColumns! - 1) as GridWidths}
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={index}
                                                        {...styles.megaMenuGridItem}
                                                        as="li"
                                                    >
                                                        <ul className="mega-menu-link--grandChildContainer">
                                                            {columnItems.map((grandChild, index) => {
                                                                const {
                                                                    children: greatGrandchildren,
                                                                    title,
                                                                    url,
                                                                } = grandChild;

                                                                return (
                                                                    // eslint-disable-next-line react/no-array-index-key
                                                                    <li
                                                                        key={index}
                                                                        className="mega-menu-link--grandChild"
                                                                    >
                                                                        <Link
                                                                            className="mega-menu-link "
                                                                            {...styles.megaMenuLink}
                                                                            onClick={this.closePopover}
                                                                            href={url}
                                                                            data-test-selector={title}
                                                                        >
                                                                            {title}
                                                                            <span className="tail">
                                                                                {"\u00a0"}
                                                                                <Icon src="ChevronRight" size={15} />
                                                                            </span>
                                                                        </Link>
                                                                        {greatGrandchildren && (
                                                                            <ul>
                                                                                {greatGrandchildren.map(
                                                                                    (greatGrandchild, index) => {
                                                                                        return (
                                                                                            // eslint-disable-next-line react/no-array-index-key
                                                                                            <li
                                                                                                key={index}
                                                                                                className="mega-menu-link--greatGrandChild"
                                                                                            >
                                                                                                <Link
                                                                                                    className="mega-menu-link"
                                                                                                    {...styles.megaMenuLink}
                                                                                                    onClick={
                                                                                                        this
                                                                                                            .closePopover
                                                                                                    }
                                                                                                    href={
                                                                                                        greatGrandchild.url
                                                                                                    }
                                                                                                    data-test-selector={
                                                                                                        greatGrandchild.title
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        greatGrandchild.title
                                                                                                    }
                                                                                                </Link>
                                                                                            </li>
                                                                                        );
                                                                                    },
                                                                                )}
                                                                            </ul>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </GridItem>
                                                );
                                            })}

                                            <GridItem
                                                className="mega-menu-grid-item mega-menu-grid-item--additionalContent "
                                                width={(12 / numberOfColumns! - 1) as GridWidths}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={index}
                                                {...styles.megaMenuGridItem}
                                                as="li"
                                            >
                                                {additionalContent && (
                                                    <>
                                                        <span
                                                            className="mega-menu-link mega-menu-link--additionalContentTitle "
                                                            {...styles.megaMenuLink}
                                                            onClick={this.closePopover}
                                                            data-test-selector={additionalContent.title}
                                                        >
                                                            {additionalContent.title}
                                                        </span>
                                                        <ul className="mega-menu-link--additionalContentContainer">
                                                            {additionalContent.links?.link?.map((content, index) => {
                                                                if (content.showAsImage.toLowerCase() === "y") {
                                                                    return (
                                                                        <li
                                                                            key={index}
                                                                            className="mega-menu-link mega-menu-link--additionalContentLink"
                                                                        >
                                                                            <Link
                                                                                className="mega-menu-link "
                                                                                {...styles.megaMenuLink}
                                                                                onClick={this.closePopover}
                                                                                href={content.url}
                                                                                data-test-selector={content.name}
                                                                                target={
                                                                                    content.openInNewTab.toLowerCase() ===
                                                                                    "y"
                                                                                        ? "_blank"
                                                                                        : ""
                                                                                }
                                                                            >
                                                                                <img
                                                                                    src={content.imageUrl}
                                                                                    alt={content.name}
                                                                                />
                                                                            </Link>
                                                                        </li>
                                                                    );
                                                                }

                                                                return (
                                                                    <li
                                                                        key={index}
                                                                        className="mega-menu-link mega-menu-link--additionalContentLink"
                                                                    >
                                                                        <Link
                                                                            className="mega-menu-link "
                                                                            {...styles.megaMenuLink}
                                                                            onClick={this.closePopover}
                                                                            href={content.url}
                                                                            data-test-selector={content.name}
                                                                        >
                                                                            {content.name}
                                                                        </Link>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    </>
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                );
                            })}
                    </GridContainer>
                    {/* END VERNDALE CUSTOMIZATION */}
                </Popover>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
